import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  Video,
  CheckCircle2,
  ShieldCheck,
  ArrowLeft,
  MessageSquare,
  RotateCcw,
} from "lucide-react";
import { track } from "@/lib/telemetry";
import { isFlagOn } from "@/lib/flags";
import { REVIEW_PRICE_FROM } from "@/lib/reviewPricing";

import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import SocialIconRow from "@/components/SocialIcons";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";

// Discovery v2 telemetry — gated on the discovery_v2 flag so toggling
// off silences emission without removing call sites.
function trackIfEnabled(name, props) {
  if (!isFlagOn("discovery_v2")) return;
  track(name, props);
}

/**
 * ExpertProfilePage — public profile at /experts/:id.
 *
 * Async only at launch (founder decision, 2026-10-09): there is no calendar,
 * no slot picker and no live-session booking here. The sidebar offers the
 * written review (with the list price floor) only for an expert the API says
 * takes written reviews (`accepts_async_review`). The three persona rows do
 * not, so their profile shows no price and no review CTA. Rating and session
 * counts are shown only when above zero.
 */
export default function ExpertProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expert, setExpert] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExpert();
    fetchReviews();
    // Re-fetch when the URL :id changes. Both fetchers are component-local
    // and close over the latest id; adding them to deps would require
    // useCallback for no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchExpert = async () => {
    try {
      const response = await axios.get(`${API}/experts/${id}`);
      setExpert(response.data);
      // Telemetry: emit profile_loaded once per profile open. The
      // reviews count is intentionally NOT awaited — `reviews` may still
      // be loading at this point; we record what we know so far. A
      // separate event could be added later for "fully loaded" if needed.
      trackIfEnabled("profile_loaded", {
        expert_id: response.data?.id,
        verified:  !!response.data?.verified,
        reviews:   response.data?.total_sessions || 0,
      });
    } catch (error) {
      console.error("Error fetching expert:", error);
      toast.error("Expert not found");
      navigate("/experts");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/expert/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  if (loading) {
    // Skeleton mirrors the loaded layout shape so the page doesn't jump
    // when data arrives: avatar block on the left, name + title bars,
    // rating row, bio block, sidebar shape on the right. Replaces a
    // full-page spinner that blocked all content (the previous UX
    // dropped the user's mental model — they clicked a face and watched
    // it disappear).
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <section
          className="pt-28 pb-16"
          aria-busy="true"
          aria-label="Loading expert profile"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="h-4 w-32 bg-slateline-200/60 rounded animate-pulse mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-8">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-40 h-40 rounded-sm bg-slateline-200/60 animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 w-20 bg-slateline-200/60 rounded animate-pulse" />
                    <div className="h-10 w-3/4 bg-slateline-200/60 rounded animate-pulse" />
                    <div className="h-5 w-1/2 bg-slateline-200/60 rounded animate-pulse" />
                    <div className="flex gap-6 pt-3">
                      <div className="h-4 w-20 bg-slateline-200/60 rounded animate-pulse" />
                      <div className="h-4 w-24 bg-slateline-200/60 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
                <div className="bg-canvas-card border border-slateline-200 p-8 rounded-sm space-y-2">
                  <div className="h-5 w-32 bg-slateline-200/60 rounded animate-pulse mb-3" />
                  <div className="h-4 w-full bg-slateline-200/60 rounded animate-pulse" />
                  <div className="h-4 w-full bg-slateline-200/60 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-slateline-200/60 rounded animate-pulse" />
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="sticky top-28 bg-canvas-card border border-slateline-200 p-6 rounded-sm h-[420px] animate-pulse" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!expert) return null;

  // Only an expert the API marks as taking written reviews gets the offer.
  const takesReviews = expert.accepts_async_review === true;

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      
      <section className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Back button */}
          <button 
            onClick={() => navigate("/experts")}
            className="flex items-center gap-2 text-slateline-500 hover:text-ink mb-8 transition-colors"
            data-testid="back-to-experts"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to experts
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Profile Header */}
              <motion.div 
                className="flex flex-col md:flex-row gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {expert.image_url && expert.image_url.length > 0 && !expert.stay_anonymous ? (
                  <div className="w-40 h-40 rounded-sm overflow-hidden" data-testid="expert-image">
                    <img 
                      src={expert.image_url.startsWith('http') ? expert.image_url : `${process.env.REACT_APP_BACKEND_URL}${expert.image_url}`}
                      alt={expert.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-brand-green/10 items-center justify-center hidden">
                      <span className="font-display text-5xl text-brand-green">
                        {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="w-40 h-40 bg-brand-green/10 rounded-sm flex items-center justify-center"
                    data-testid="expert-image"
                  >
                    <span className="font-display text-5xl text-brand-green">
                      {expert.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {expert.verified && (
                      <span className="inline-flex items-center gap-1 text-xs bg-brand-green/10 text-brand-green px-2 py-1 rounded">
                        <CheckCircle2 className="w-3 h-3" />
                        VERIFIED
                      </span>
                    )}
                  </div>
                  <h1 className="font-display text-4xl mb-2" data-testid="expert-name">{expert.name}</h1>
                  <p className="text-xl text-slateline-500 mb-4" data-testid="expert-title">{expert.title}</p>
                  
                  {(Number(expert.rating) > 0 || Number(expert.total_sessions) > 0) && (
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 fill-brand-green text-brand-green" />
                        <span className="font-semibold">{expert.rating}</span>
                        <span className="text-slateline-400">({reviews.length} reviews)</span>
                      </div>
                      <div className="flex items-center gap-2 text-slateline-500">
                        <Video className="w-5 h-5" />
                        <span>{expert.total_sessions} reviews done</span>
                      </div>
                    </div>
                  )}

                  {/* Social Links — shared SocialIconRow component renders
                      LinkedIn / GitHub / X(Twitter) consistently across the
                      profile page, the picker modal, and the browse cards. */}
                  {expert.stay_anonymous ? (
                    <div className="mt-4">
                      <button
                        onClick={() => toast.info("This expert has chosen to remain anonymous")}
                        className="text-xs text-slateline-400 italic cursor-not-allowed"
                        title="Expert is anonymous"
                      >
                        Profile links hidden — anonymous expert
                      </button>
                    </div>
                  ) : (
                    <SocialIconRow expert={expert} className="mt-4" />
                  )}
                </div>
              </motion.div>

              {/* Bio */}
              <motion.div 
                className="bg-canvas-card border border-slateline-200 p-8 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-display text-2xl mb-4">About</h2>
                <p className="text-slateline-600 leading-relaxed" data-testid="expert-bio">{expert.bio}</p>
              </motion.div>

              {/* Expertise & Industries */}
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                  <h3 className="font-display text-xl mb-4">Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {(expert.expertise || []).map((exp, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1.5 bg-brand-green/10 text-brand-green text-sm rounded-full"
                      >
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                  <h3 className="font-display text-xl mb-4">Industries</h3>
                  <div className="flex flex-wrap gap-2">
                    {(expert.industries || []).map((ind, i) => (
                      <span 
                        key={i} 
                        className="px-3 py-1.5 bg-slateline-100 text-slateline-700 text-sm rounded-full"
                      >
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Reviews */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-display text-2xl mb-6">Reviews ({reviews.length})</h2>
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className="bg-canvas-card border border-slateline-200 p-6 rounded-sm"
                      >
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'fill-brand-green text-brand-green' : 'text-slateline-300'}`}
                              />
                            ))}
                          </div>
                          <span className="text-slateline-400 text-sm">{review.user_name}</span>
                        </div>
                        {review.comment && (
                          <p className="text-slateline-600">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-canvas-card border border-slateline-200 p-8 rounded-sm text-center">
                    <MessageSquare className="w-12 h-12 text-slateline-300 mx-auto mb-4" />
                    <p className="text-slateline-500">No reviews yet.</p>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar: written review only. No calendar, no hourly rate. */}
            <div className="lg:col-span-1">
              {/* The public profile no longer carries user_id (F5); /auth/me
                  gives the signed-in user's expert_id instead. */}
              {user && user.expert_id && user.expert_id === expert.id ? (
                <motion.div 
                  className="sticky top-28 bg-canvas-card border border-slateline-200 p-6 rounded-sm"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="text-center py-4">
                    <p className="text-slateline-500 mb-2">This is your own profile</p>
                    <Button 
                      onClick={() => navigate("/expert-dashboard")}
                      variant="link"
                      className="text-brand-green hover:text-brand-greenHover"
                    >
                      Go to your dashboard →
                    </Button>
                  </div>
                </motion.div>
              ) : takesReviews ? (
                <motion.div 
                  className="sticky top-28 space-y-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="bg-white border border-slateline-200 rounded-sm p-6" data-testid="written-review-panel">
                    <div className="text-[10px] uppercase tracking-wider text-slateline-400 font-bold">
                      Written review
                    </div>
                    <div className="font-display text-3xl text-ink mt-1">From ${REVIEW_PRICE_FROM}</div>
                    <p className="text-sm text-slateline-600 mt-2 leading-relaxed">
                      24 hours, 48 hours or 1 week. You send your work and the AI
                      conversation behind it, and get a written verdict: findings,
                      what to change, and how confident the call is.
                    </p>
                    <Button
                      onClick={() => {
                        trackIfEnabled("profile_book_clicked", {
                          expert_id: id, has_slot: false,
                        });
                        navigate("/get-verified");
                      }}
                      className="w-full mt-5 bg-brand-green hover:bg-brand-greenHover text-white font-semibold py-6 rounded-sm btn-shine"
                      data-testid="get-reviewed-btn"
                    >
                      Get your work reviewed
                    </Button>
                  </div>

                  <div className="bg-white border border-slateline-200 rounded-sm p-4 space-y-2.5">
                    <div className="flex items-start gap-2.5">
                      <ShieldCheck className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-xs text-ink leading-relaxed">
                        <span className="font-semibold">A review is one named person's opinion</span> on your work. It is not a certification.
                      </p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <RotateCcw className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-xs text-ink leading-relaxed">
                        <span className="font-semibold">Cancel before work starts for a full refund.</span> Other cases are handled under our Terms (section 4.3).
                      </p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div 
                  className="sticky top-28 bg-white border border-slateline-200 rounded-sm p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  data-testid="no-review-panel"
                >
                  <p className="text-sm text-slateline-600 leading-relaxed">
                    {/* `false` is an explicit "no"; a missing field (an API that
                        predates it) says nothing about this person. */}
                    {expert.accepts_async_review === false
                      ? `${expert.name} isn't taking review requests through Xpert. `
                      : ""}
                    Today, written reviews are done by Ankit Kumar, the founder.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      <MarketingFooter />
    </div>
  );
}
